// @ts-check
import React, { useMemo, useRef, useState } from "react"
import Card from "../../../../components/atoms/Card.atom"
import DataTable from "../../../../components/atoms/table/DataTable.atom"
import ContentLayoutHeader from "../../../../components/layouts/ContentLayoutHeader.layout"
import Column from "../../../../components/atoms/table/Column.atom"
import VacationFlag from "../../../../components/molecules/VacationFlag.molecule"
import VacationDayVariantChip from "../../../../components/molecules/VacationDayVariantChip.molecule"
import VacationPendingRequestCard from "../../components/VacationPendingRequestCard.compound"
import TeamMemberSortFilter from "../../../../components/compounds/columnFilters/TeamMemberSortFilter.compound"
import { groupBy, sort } from "../../../../utils/arrays.utils"
import useFetchTeamMembersVacationsReport from "./hooks/useFetchTeamMembersVacationsReport"
import Tooltip from "../../../../components/atoms/Tooltip.atom"
import Button from "../../../../components/atoms/Button.atom"
import Modal from "../../../../components/atoms/Modal.atom"
import { OverlayPanel } from "../../../../components/atoms/OverlayPanel.atom"
import TeamMemberVacationsBucketTable from "../common/TeamMemberVacationsBucketTable.controller"
import { ReactComponent as BucketIcon } from "../../../../design/assests/images/icons/glass_cup.svg"
import { ReactComponent as CalendarClockIcon } from "../../../../design/assests/images/icons/calendar_clock.svg"
import PendingRequestsColumnBody from "../../components/VacationsReportPendingRequestColumn.compund"

import { DateTime } from "luxon"
import { ContextMenu } from "primereact/contextmenu"
import AttachFileIcon from "@mui/icons-material/AttachFileOutlined"

import ModalFormVacationRequestReview from "../management/ModalFormVacationRequestReview.controller"
import ModalFormApprovedRequestReview from "../management/ModalFormApprovedRequestReview.controller"
import ModalFormRejectedRequestReview from "../management/ModalFormRejectedRequestReview.controller"
import ModalFormChangeRequestReview from "../management/ModalFormChangeRequestReview.controller"
import ModalFormCancelRequestReview from "../management/ModalFormCancelRequestReview.controller"

import {
  VACATION_STATES,
  VACATION_SCHEMAS,
} from "../../../../consts/modules/vacations.consts"
import { getVacationState } from "../../../../utils/vacation.utils"
import ModalFormAditionalDaysReview from "../myVacations/ModalFormAditionalDaysReview.controller"
import { useNavigate } from "react-router-dom"
import { isDefined } from "../../../../utils/predicates.utils"
import { clsx } from "../../../../utils/strings.utils"

export default function VacationsReport() {
  /** @type {React.MutableRefObject<any>} */
  const overlayRef = useRef(null)
  /** @type {[VacationRequest[], Function]} */
  const [overlayRequests, setOverlayRequests] = useState([])
  const [visibleRequestModal, setVisibleRequestModal] = useState(false)
  const [selectedVacationRequest, setSelectedVacationRequest] = useState(null)
  const [refresh, setRefresh] = useState(false)
  const triggerRefresh = () => setRefresh(!refresh)

  const [activeTeamMember, setActiveTeamMember] = useState(
    /** @type {TeamMemberVacationReport|null} */ (null)
  )
  const { teamMembers, isLoading } = useFetchTeamMembersVacationsReport(refresh)
  const [showBucketModal, setShowBucketModal] = useState(false)
  const [isModalAditionalDaysOpen, setIsModalAditionalDaysOpen] =
    useState(false)
  const cmRef = useRef(/** @type {ContextMenu|null} */ (null))
  const navigate = useNavigate()

  const teamMemberHasBucket =
    activeTeamMember?.holidaySchema.name !== VACATION_SCHEMAS.INTERNATIONAL

  

  const buttonHeaderProps = {
    label: "Nuevo día adicional",
    icon: <AttachFileIcon className="mr-2" style={{ transform: 'rotate(45deg)' }} />,
    className: "h-fit vacations-new-button",
    onClick: (e) => {
      setActiveTeamMember(null)
      setIsModalAditionalDaysOpen(true)
    }
  }

  const cmRowOptions = [
    {
      label: "Ver días adicionales",
      icon: <i className="pi pi-paperclip text-black mr-2" />,
      command: () => {
        if (activeTeamMember) {
          setIsModalAditionalDaysOpen(true)
        }
      },
    },
    teamMemberHasBucket ?
      {
        label: "Ver cubeta",
        icon: <BucketIcon className="text-black mr-2" width={20} height={20} />,
        command: () => {
          if (activeTeamMember) {
            setShowBucketModal(true)
          }
        },
      }
    : null,
    {
      label: "Ver historial de vacaciones",
      icon: (
        <CalendarClockIcon
          className="calendar-clock-icon text-black mr-2"
          width={20}
          height={20}
        />
      ),
      command: () => {
        if (activeTeamMember) {
          navigate(
            `/vacaciones/historial-vacaciones/${activeTeamMember.idTeamMember}`
          )
        }
      },
    },
  ].filter(isDefined)

  const teamMembersFilter = useMemo(
    () =>
      sort(
        Object.values(groupBy(teamMembers, "isActiveUser")).map((group) => ({
          active: group[0].isActiveUser,
          items: group.map((member) => ({
            label: member.userName,
            value: member.userId,
          })),
        })),
        "active",
        "desc"
      ),
    [teamMembers]
  )

  /**
   * @param {TeamMemberVacationReport} teamMemberVacation
   */
  function getTeamMemberNameColumnBody(teamMemberVacation) {
    return (
      <div className="flex gap-2 align-items-center">
        <VacationFlag schemaTitle={teamMemberVacation.holidaySchema.name} />
        <span>{teamMemberVacation.teamMemberNameTag}</span> -
        <span>{teamMemberVacation.userName}</span>
      </div>
    )
  }

  /**
   * @param {TeamMemberVacationReport} teamMemberVacation
   */
  function getCurrentPeriodColumnBody(teamMemberVacation) {
    return `${teamMemberVacation.currentPeriod.start} - ${teamMemberVacation.currentPeriod.end}`
  }

  /**
   * @param {TeamMemberVacationReport} teamMemberVacation
   */
  function getDaysOffColumnBody(teamMemberVacation) {
    const bucketDay = teamMemberVacation.daysOff.find(
      (day) => day.type === "cubeta"
    )
    const accordedDay = teamMemberVacation.daysOff.find(
      (day) => day.type === "dia_acordado"
    )
    const plusDay = teamMemberVacation.daysOff.find(
      (day) => day.type === "dia_plus"
    )
    const extraDay = teamMemberVacation.daysOff.find(
      (day) => day.type === "dia_extra"
    )

    return (
      <div className="flex gap-4 align-items-center taken-days-column">
        {/* Columna 1: Cubeta o Días acordados (siempre visible) */}
        <div className="day-type-column">
          {bucketDay && bucketDay.available !== null && bucketDay.max > 0 ?
            <TeamMemberBucketDaysResume
              typeOfDayOf={bucketDay}
              teamMember={teamMemberVacation}
              onBucketClick={() => {
                setActiveTeamMember(teamMemberVacation)
                setShowBucketModal(true)
              }}
            />
          : accordedDay && accordedDay.max > 0 ?
            <TeamMemberDaysResume
              type={accordedDay.type}
              used={accordedDay.used}
              max={accordedDay.max}
              available={accordedDay.available}
            />
          : null}
        </div>

        {/* Columna 2: Días plus (con visibility hidden si no tiene) */}
        <div className="day-type-column">
          {typeof plusDay?.available === "number" && plusDay.max > 0 ? (
          <TeamMemberDaysResume
            type="dia_plus"
            used={plusDay?.used || 0}
            max={plusDay?.max || 0}
            available={plusDay?.available || 0}
            tooltip={
              plusDay && plusDay.max > 0 ?
                `Fecha de vencimiento: ${teamMemberVacation.currentPeriod.end}`
              : undefined
            }
          />
           ) : null}
        </div>

        {/* Columna 3: Días adicionales (con visibility hidden si no tiene) */}
      <div className="day-type-column">
          {extraDay && extraDay.available > 0 ? (
          <TeamMemberDaysResume
            type="dia_extra"
            used={extraDay?.used || 0}
            max={extraDay?.max || 0}
            available={extraDay?.available || 0}
            tooltip={
              extraDay && extraDay.available > 0 ?
                "Click para ver días adicionales"
              : undefined
            }
            teamMember={teamMemberVacation}
            onAdditionalDaysClick={() => {
              setActiveTeamMember(teamMemberVacation)
              setIsModalAditionalDaysOpen(true)
            }}
          />
           ) : null}
        </div>
      </div>
    )
  }

  /**
   * @param {TeamMemberVacationReport} teamMember
   */
  function getHeaderBody(teamMember) {
    return (
      <div className="flex gap-3 align-items-center">
        <BucketIcon className="text-info" width={32} height={32} />
        <div className="flex flex-column">
          <span className="text-3xl font-bold font-cairo leading-[1.5]">Resumen de cubeta</span>

          <div className="font-semibold flex gap-3 font-cairo">
            <div className="flex gap-2 align-items-center">
              <VacationFlag schemaTitle={teamMember.holidaySchema.name} />
              <span>{teamMember.userName}</span>
            </div>

            <div className="flex gap-2 align-items-center">
              <i className="pi pi-calendar !text-black" />
              <span>
                {teamMember.userCurrentYear}{" "}
                {teamMember.userCurrentYear > 1 ? "años" : "año"}
              </span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  /**
   * @param {boolean} visibleModal
   * @param {VacationRequest|null} selectedVacationRequest
   * @param {string[]} keys
   */
  const showModal = (visibleModal, selectedVacationRequest, keys) => {
    if (!visibleModal || !selectedVacationRequest || !keys) return false

    const vacationStatus = getVacationState(selectedVacationRequest)

    return visibleModal && keys.includes(vacationStatus)
  }

  const renderVacationRequests = () => {
    if (!activeTeamMember) return null

    return (
      overlayRequests?.map((req) => (
        <VacationPendingRequestCard
          key={req.id}
          vacationRequest={req}
          mode="team-member"
          onClick={() => {
            setVisibleRequestModal(true)
            setSelectedVacationRequest(req)
            overlayRef.current?.hide()
          }}
        />
      )) ?? null
    )
  }

  const handleCloseAdditionalDaysModal = () => {
  setIsModalAditionalDaysOpen(false)
  setActiveTeamMember(null) 
}

  return (
    <>
      <OverlayPanel
        ref={overlayRef}
        className="overlay-requests"
        showCloseIcon
        dismissable={false}
      >
        {activeTeamMember && (
          <div className="overlay-member-name">{activeTeamMember.userName}</div>
        )}
        {renderVacationRequests()}
      </OverlayPanel>

      <ContentLayoutHeader 
        title="Reporte de vacaciones"
        buttonProps={buttonHeaderProps}
      />

      <Card className="vacations-report-card shadow-none p-1">
        <DataTable
          className="hoverable-table-row"
          value={teamMembers}
          responsiveLayout="scroll"
          scrollable
          style={{ height: "100%" }}
          scrollHeight="100%"
          onContextMenu={({ originalEvent, data }) => {
            cmRef.current?.show(originalEvent)
            setActiveTeamMember(data)
          }}
          loading={isLoading}
          loadingConfig={{ qtyOfRows: 15 }}
        >
          <Column
            field="userId"
            header="Integrantes"
            body={getTeamMemberNameColumnBody}
            filter
            showFilterMatchModes={false}
            showFilterMenuOptions={false}
            filterMatchMode="in"
            filterElement={(options) => {
              return (
                <div className="p-multiselect-item-group-hidden">
                  <TeamMemberSortFilter
                    value={options.value || []}
                    onChange={(/** @type {unknown[]} */ value) => {
                      options.filterCallback(value)
                    }}
                    teamMembers={teamMembersFilter}
                  />
                </div>
              )
            }}
            style={{ minWidth: "260px" }}
          />
          <Column
            field="userCurrentYear"
            header="Año que cubre"
            style={{ maxWidth: "120px" }}
          />
          <Column
            field="currentPeriod"
            header="Periodo actual"
            body={getCurrentPeriodColumnBody}
            style={{ maxWidth: "230px" }}
          />
          <Column
            field="daysOff"
            header="Días disponibles"
            body={getDaysOffColumnBody}
          />

          <Column
            bodyClassName="pending-requests-column"
            headerClassName="pending-requests-column"
            header="Solicitudes pendientes"
            body={(data) => (
                <PendingRequestsColumnBody
                  data={data}
                  overlayRef={overlayRef}
                  setOverlayRequests={setOverlayRequests}
                  setActiveTeamMember={setActiveTeamMember}
                  setSelectedVacationRequest={setSelectedVacationRequest}
                  setVisibleRequestModal={setVisibleRequestModal}
                  cmRef={cmRef}
                />
              )}        
          />
        </DataTable>
      </Card>

      <ContextMenu
        model={cmRowOptions}
        className="cm-row-options"
        ref={cmRef}
      />

      <Modal
        visible={showBucketModal}
        onHide={() => setShowBucketModal(false)}
        closeOnEscape
        header={() => {
          if (!activeTeamMember) return null

          return getHeaderBody(activeTeamMember)
        }}
        style={{ width: "624px", maxWidth: "90vw" }}
        draggable={false}
        className="pb-2"
      >
        <TeamMemberVacationsBucketTable
          teamMemberId={activeTeamMember?.idTeamMember}
          workAnniversary={activeTeamMember?.userWorkAnniversary || undefined}
          periodStartDate={
            activeTeamMember?.currentPeriod.start ?
              DateTime.fromFormat(
                activeTeamMember?.currentPeriod.start,
                "dd/MM/yyyy"
              ).toISO()
            : undefined
          }
          periodEndDate={
            activeTeamMember?.currentPeriod.end ?
              DateTime.fromFormat(
                activeTeamMember?.currentPeriod.end,
                "dd/MM/yyyy"
              )
                .endOf("month")
                .toISO()
            : undefined
          }
        />
      </Modal>
      <ModalFormVacationRequestReview
        visible={showModal(visibleRequestModal, selectedVacationRequest, [
          VACATION_STATES.PENDING,
        ])}
        setVisible={setVisibleRequestModal}
        selectedVacationRequest={selectedVacationRequest}
        triggerRefresh={triggerRefresh}
      />

      <ModalFormApprovedRequestReview
        visible={showModal(visibleRequestModal, selectedVacationRequest, [
          VACATION_STATES.APPROVED,
          VACATION_STATES.APPROVED_AND_PENDING_PLUS_DAYS_PAY_DATE,
        ])}
        setVisible={setVisibleRequestModal}
        selectedVacationRequest={selectedVacationRequest}
        triggerRefresh={triggerRefresh}
      />

      <ModalFormRejectedRequestReview
        visible={showModal(visibleRequestModal, selectedVacationRequest, [
          VACATION_STATES.REJECTED,
          VACATION_STATES.CANCELED,
        ])}
        setVisible={setVisibleRequestModal}
        selectedVacationRequest={selectedVacationRequest}
      />

      <ModalFormChangeRequestReview
        visible={showModal(visibleRequestModal, selectedVacationRequest, [
          VACATION_STATES.CHANGE_REQUEST,
        ])}
        setVisible={setVisibleRequestModal}
        selectedVacationRequest={selectedVacationRequest}
        triggerRefresh={triggerRefresh}
      />

      <ModalFormCancelRequestReview
        visible={showModal(visibleRequestModal, selectedVacationRequest, [
          VACATION_STATES.CANCEL_REQUEST,
        ])}
        setVisible={setVisibleRequestModal}
        selectedVacationRequest={selectedVacationRequest}
        triggerRefresh={triggerRefresh}
      />
      <ModalFormAditionalDaysReview
        visible={isModalAditionalDaysOpen}
        setVisible={handleCloseAdditionalDaysModal}
        selectedTeamMember={activeTeamMember || {}}
        triggerRefresh={triggerRefresh}
        activeTeamMember={activeTeamMember}
        setActiveTeamMember={setActiveTeamMember}
        refresh={refresh}
      />
    </>
  )
}

/**
 * @param {{ typeOfDayOf: TeamMemberTypeOfDayOff, teamMember: TeamMemberVacationReport, onBucketClick: (teamMember: TeamMemberVacationReport) => void }} param0
 */
function TeamMemberBucketDaysResume({
  typeOfDayOf,
  teamMember,
  onBucketClick,
}) {
  return (
    <div className="flex gap-2 align-items-center" style={{ minWidth: "auto" }}>
      <Button
        className=" p-button-link p-0"
        onClick={() => onBucketClick(teamMember)}
      >
        <Tooltip
          target=".bucket-days-resume-chip"
          content="Click para ver resumen de cubeta"
        />
        <div className="bucket-days-resume-chip">
          <VacationDayVariantChip variant="long" dayType="cubeta" />
        </div>
      </Button>
      <span
        className={typeOfDayOf.available < 0 ? "bucket-available--negative" : ""}
      >
        {typeOfDayOf.available}
      </span>
    </div>
  )
}

/**
 * @param {TeamMemberTypeOfDayOff & { tooltip?: string, teamMember?: TeamMemberVacationReport, onAdditionalDaysClick?: () => void }} param0
 */
function TeamMemberDaysResume({
  type,
  used,
  max,
  available,
  tooltip,
  teamMember,
  onAdditionalDaysClick,
}) {
  const randomId = `day-resume-chip-${crypto.randomUUID()}`
  return (
    <div className="flex gap-2 align-items-center vacation-report">
      {tooltip ?
        <Tooltip target={`.day-resume-chip-${randomId}`} content={tooltip} />
      : null}
      <div
        className={`day-resume-chip-${randomId} ${type === "dia_extra" ? "cursor-pointer" : ""}`}
        onClick={() => {
          if (type === "dia_extra" && onAdditionalDaysClick) {
            onAdditionalDaysClick()
          }
        }}
      >
        <VacationDayVariantChip variant="long" dayType={type} />
      </div>
      <span>{available}</span>
    </div>
  )
}

/**
 * @typedef {Object} HolidaySchema
 * @property {"Mexicano" | "Nacional v2019" | "Nacional v2024" | "Extranjero" | "Internacional" | "Sisu" | "Global"} name
 * @property {number} id
 */

/**
 * @typedef {Object} TeamMemberVacationReport
 * @property {number} userId
 * @property {string} userName
 * @property {number|null} userWorkAnniversary
 * @property {number} isActiveUser
 * @property {number} userCurrentYear
 * @property {number} idTeamMember
 * @property {string} teamMemberNameTag
 * @property {{start: string|null, end: string|null}} currentPeriod
 * @property {HolidaySchema} holidaySchema
 * @property {Array<TeamMemberTypeOfDayOff>} daysOff
 */

/**
 * @import * as VACATION_CONSTS from "@/consts/modules/vacations.consts"
 *
 * @typedef {Object} TeamMemberTypeOfDayOff
 * @property {VACATION_CONSTS.VACATION_DAY_TYPES[keyof VACATION_CONSTS.VACATION_DAY_TYPES]} type - Tipo de día tomado (e.g., "Vacaciones", "Día personal").
 * @property {number} available - Número de días disponibles de este tipo.
 * @property {number} used - Número de días utilizados de este tipo.
 * @property {number} max - Número de días totales de este tipo.
 */
